import { Card, Flex, Typography } from "antd"
import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router"

import { useLeadActivityFormat } from "../../../../../Lead/LeadWorkspacePage/activity/use-lead-activity-format.ts"
import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { fetchProjectLogHistory } from "../../../../redux/project.thunks.ts"
import { ProjectLogsFilters } from "./ProjectLogsFilters.tsx"
import { ProjectLogsTable } from "./ProjectLogsTable.tsx"
import {
  NO_LOG_FILTERS,
  filterProjectLogs,
  projectLogActionOptions,
  projectLogTypeOptions,
  projectLogUserOptions,
  sortLogsNewestFirst
} from "./project-logs.ts"
import type { ProjectLogFilters } from "./project-logs.types.ts"

export default function ProjectViewLogsSection() {
  const projectId = useParams()?.id as string
  const dispatch = useAppDispatch()
  const { dateFormat, timezone } = useLeadActivityFormat()

  const logs = useAppSelector((state) => state.project.logs)
  const loading = useAppSelector((state) => state.project.fetchingSection)

  const [filters, setFilters] = useState<ProjectLogFilters>(NO_LOG_FILTERS)

  useEffect(() => {
    void dispatch(fetchProjectLogHistory(projectId))
  }, [dispatch, projectId])

  const entries = useMemo(
    () => sortLogsNewestFirst(logs ?? [], dateFormat, timezone),
    [logs, dateFormat, timezone]
  )
  const visible = useMemo(
    () => filterProjectLogs(entries, filters, dateFormat, timezone),
    [entries, filters, dateFormat, timezone]
  )

  const actionOptions = useMemo(() => projectLogActionOptions(entries), [entries])
  const typeOptions = useMemo(() => projectLogTypeOptions(entries), [entries])
  const userOptions = useMemo(() => projectLogUserOptions(entries), [entries])

  return (
    <Card size={"small"} data-guide-target="project-activity-panel">
      <Flex vertical gap={"middle"}>
        <Flex vertical gap={2}>
          <Typography.Title level={4} className={"m-0"}>
            Change History
          </Typography.Title>
          <Typography.Text type={"secondary"} className={"text-sm"}>
            Audit trail of all changes made to this project
          </Typography.Text>
        </Flex>

        <ProjectLogsFilters
          filters={filters}
          actionOptions={actionOptions}
          typeOptions={typeOptions}
          userOptions={userOptions}
          onChange={(patch) =>
            setFilters((current) => ({ ...current, ...patch }))
          }
        />

        <ProjectLogsTable
          entries={visible}
          loading={loading}
          filtered={visible.length !== entries.length}
          dateFormat={dateFormat}
          timezone={timezone}
        />
      </Flex>
    </Card>
  )
}
